import * as React from 'react';
import { Trans } from 'react-i18next';
import { Field } from 'react-final-form';

// Utils
import { t } from '@toolkit/util/i18n';
// Constants
import { VEHICLE_TYPES } from '@src/shared/src/const/app';
import { checked } from '@toolkit/util/formsValidation';
// Actions
// Models
// Interfaces
// Components
import { FormCheckbox } from '@src/toolkit/ui';
// Styles
import '../styles/LegalNotice.scss';
import { getProviderOrder, ProviderKey } from './legalNoticeUtils';

type Props = {
  operators: string[];
  vehicles: string[];
  aggregators: string[];
  hasNonExpediaHotelOrRentalItems: boolean;
};

// Builds a readable provider list like "SBB, SNCF and DB" using localized separators.
const joinProviders = (providers: React.ReactNode[]) => {
  if (!providers?.length) return null;

  const parts: React.ReactNode[] = [];
  providers.forEach((provider, index) => {
    if (index > 0) {
      parts.push(
        <React.Fragment key={`sep-${index}`}>
          {index === providers.length - 1
            ? t('checkoutPayment.text.legalNoticeV2.providersSeparatorLast')
            : t('checkoutPayment.text.legalNoticeV2.providersSeparator')}
        </React.Fragment>,
      );
    }
    parts.push(
      <React.Fragment key={`provider-${index}`}>
        {provider}
      </React.Fragment>,
    );
  });
  return <>{parts}</>;
};

const CheckoutPaymentLegalNoticeForm: React.FC<Props> = (props: Props) => {
  const tcpTermsUrl = t('checkoutPayment.legalNotice.link.tcp.generalTerms');
  const expediaTermsUrl = t('checkoutPayment.legalNotice.link.expediaHotel.generalTerms');
  const dbGeneralTermsUrl = t('checkoutPayment.legalNotice.link.db.generalTerms');
  const dbTermsOfUseUrl = t('checkoutPayment.legalNotice.link.db.termOfUse');

  const providerOrder = getProviderOrder({
    operators: props.operators,
    aggregators: props.aggregators,
    hasNonExpediaHotelOrRentalItems: props.hasNonExpediaHotelOrRentalItems,
  });
  const hasFlight = props.vehicles.includes(VEHICLE_TYPES.PLANE);

  const providerNodes: React.ReactNode[] = [];
  providerOrder.forEach((provider: ProviderKey) => {
    switch (provider) {
      case 'other':
        providerNodes.push(
          t('checkoutPayment.text.legalNoticeV2.provider.other'),
        );
        break;
      case 'sbb':
        providerNodes.push(
          t('checkoutPayment.text.legalNoticeV2.provider.sbb'),
        );
        break;
      case 'sncf':
        providerNodes.push(
          t('checkoutPayment.text.legalNoticeV2.provider.sncf'),
        );
        break;
      case 'db':
        providerNodes.push(
          <Trans i18nKey="checkoutPayment.text.legalNoticeV2.provider.db">
            <a
              href={dbGeneralTermsUrl}
              target="_blank"
              className="is-bold">
              generalTerms
            </a>
            <a
              href={dbTermsOfUseUrl}
              target="_blank"
              className="is-bold">
              termsOfUse
            </a>
          </Trans>,
        );
        break;
      case 'expedia':
        providerNodes.push(
          <Trans i18nKey="checkoutPayment.text.legalNoticeV2.provider.expedia">
            <a
              href={expediaTermsUrl}
              target="_blank"
              className="is-bold">
              generalTerms
            </a>
          </Trans>,
        );
        break;
    }
  });

  const mainNotice = (
    <>
      {t('checkoutPayment.text.legalNoticeV2.dynamicPrefix')}
      {joinProviders(providerNodes) ?? t('checkoutPayment.text.legalNoticeV2.provider.other')}
      .
      {' '}
      <Trans i18nKey="checkoutPayment.text.legalNoticeV2.dynamicSuffix">
        <a
          href={tcpTermsUrl}
          target="_blank"
          className="is-bold">
          generalTerms
        </a>
      </Trans>
    </>
  );

  return (
    <div className="checkout-box box tcp-legal-notice">
      <ul>
        <li>{mainNotice}</li>
        {hasFlight && (
          <li>
            <Trans
              i18nKey="checkoutPayment.text.legalNotice.airlinesV2">
              <a
                href="https://www.iata.org/contentassets/6fea26dd84d24b26a7a1fd5788561d6e/dgr-62-de-2.3a.pdf"
                target="_blank"
                className="is-bold">
                link
              </a>
            </Trans>
          </li>
        )}
      </ul>
      <Field
        id="accept"
        name="legalNotice"
        label={t('checkoutPaymentInfo.checkbox.acceptTermsV2')}
        labelCSS="is-bold"
        component={FormCheckbox}
        type="checkbox"
        wrapperCSS="tcp-legal-notice-terms"
        validate={checked}
      />
    </div>
  );
};

export default CheckoutPaymentLegalNoticeForm;
